import { Dialog, TableRow } from '@material-ui/core';
import { Alert, Button, Checkbox, Chip, DialogActions, Table, DialogContent, DialogContentText, DialogTitle, TableBody, TableCell, TableContainer, TableHead, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import MissionTransportService from '../../../services/MissionTransportService';
import Message from '../../tools/Message';
import LoadingCustomBackdrop from '../../tools/LoadingCustomBackdrop';

const isFidTransport = (transport = {}) =>
  [transport?.vhc_type, transport?.type].some(
    (transportType) =>
      String(transportType || '').trim().toUpperCase() === 'FID'
  );

const hasValidDriver = (transport = {}) => {
  const driverId = Number(transport?.driver_id);
  return Number.isFinite(driverId) && driverId > 0;
};

const normalizeTransportId = (id) => {
  const transportId = Number(id);
  return Number.isFinite(transportId) && transportId > 0
    ? transportId
    : null;
};

const hasValidTransportId = (transport = {}) =>
  normalizeTransportId(transport?.transport_id) !== null;

const normalizeTransportIds = (ids = []) =>
  Array.from(
    new Set(
      ids
        .map(normalizeTransportId)
        .filter((id) => id !== null)
    )
  );

const ConfirmationTransport = (props) => {
    const { openDialog, mission_planning_id, mission_planning_agenda_id, mission_timesheet_id, reference, closeDialog, onConfirm} = props;
    const navigate = useNavigate();
    const [message, setMessage] = useState({
          open: false,
          color: 'green',
          messageInfo: '',
        });
    const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [transportLoading, setTransportLoading] = useState(false);
    const [transports, setTransports] = useState([]);
    const [carpooling, setCarpooling] = useState(false);
    const [dialogDriver, setDialogDriver] = useState(false);
    const [selectedTransportIds, setSelectedTransportIds] = useState([]);
    const [timesheetValidated, setTimesheetValidated] = useState(false);
    const [omSendFailed, setOmSendFailed] = useState(false);
    const { open, color, messageInfo } = message;
    const [textLoading, setTextLoading] = useState('');
    const transportRequestIdRef = useRef(0);
    const handleCloseMessage = () => {setMessage({ ...message, open: false });};
    const resetDialogState = useCallback(() => {
      setTransports([]);
      setCarpooling(false);
      setSelectedTransportIds([]);
      setDialogDriver(false);
      setTimesheetValidated(false);
      setOmSendFailed(false);
      setLoadingButton(false);
      setTransportLoading(false);
      setOpenLoadingSimpleBackdrop(false);
      setTextLoading('');
    }, []);
    const handleCloseAll = useCallback(() => {
      transportRequestIdRef.current += 1;
      resetDialogState();
      closeDialog();
    }, [closeDialog, resetDialogState]);
    const STATE_TIMESHEET = [
    { label: 'Nouveau', value: null, color: '#33ccff' },
    { label: 'Élaboré', value: 'PLANNING', color: 'orange' },
    { label: 'En attente de validation', value: 'PENDING', color: 'purple' },
    { label: 'Validé', value: 'CONFIRMED', color: 'green' },
    { label: 'Refusé', value: 'REFUSED', color: 'red' },
    { label: 'Tous', value: '', color: '#33ccff' }
  ];

    const fetchTransportDetails = useCallback(async () => {
        const requestId = transportRequestIdRef.current + 1;
        transportRequestIdRef.current = requestId;
        setTransportLoading(true);
        try {
            const transportResult = await MissionTransportService.transportsByMissionPlanning(mission_planning_id, mission_planning_agenda_id);
            if (requestId !== transportRequestIdRef.current) {
              return [];
            }
            if (transportResult && transportResult.status) {
              const transportData = Array.isArray(transportResult.data) ? transportResult.data : [];
              const transportsWithMissions = transportData.map((transport) => ({
                ...transport,
                missions: Array.isArray(transport?.missions)
                  ? transport.missions
                  : [],
              }));

              setTransports(transportsWithMissions);
              setCarpooling(
                transportsWithMissions.some(
                  (transport) => transport.missions.length > 1
                )
              );
              return transportsWithMissions;
            }

            setMessage({
              open: true,
              color: 'red',
              messageInfo: transportResult?.message || 'Erreur lors de la récupération des transports.'
            });
            return [];
        } catch (error) {
            if (requestId !== transportRequestIdRef.current) {
              return [];
            }
            console.error('Error fetching transport details:', error);
            setMessage({
              open: true,
              color: 'red',
              messageInfo: "Erreur lors de la récupération des transports."
            });
            return [];
        } finally {
            if (requestId === transportRequestIdRef.current) {
              setTransportLoading(false);
            }
        }
    }, [mission_planning_agenda_id, mission_planning_id]);

    useEffect(() => {
        if (!openDialog) {
          transportRequestIdRef.current += 1;
          resetDialogState();
          return undefined;
        }

        fetchTransportDetails();

        return () => {
          transportRequestIdRef.current += 1;
        };
    }, [fetchTransportDetails, openDialog, resetDialogState]);

    const fidTransports = transports.filter(isFidTransport);
    const fidTransportsEligibleForOm = fidTransports.filter(
      (transport) => hasValidDriver(transport) && hasValidTransportId(transport)
    );
    const fidTransportsWithoutDriver = fidTransports.filter(
      (transport) => !hasValidDriver(transport)
    );
    const fidTransportIds = normalizeTransportIds(
      fidTransports.map((transport) => transport.transport_id)
    );
    const allFidSelected = fidTransportIds.length > 0
      && fidTransportIds.every((transportId) => selectedTransportIds.includes(transportId));
    const someFidSelected = fidTransportIds.some(
      (transportId) => selectedTransportIds.includes(transportId)
    );

    const handleToggleTransport = (transportId) => {
      const normalizedTransportId = normalizeTransportId(transportId);
      if (normalizedTransportId === null) {
        return;
      }

      setSelectedTransportIds((previousIds) => {
        if (previousIds.includes(normalizedTransportId)) {
          return previousIds.filter((id) => id !== normalizedTransportId);
        }
        return [...previousIds, normalizedTransportId];
      });
    };

    const continueAfterSuccessfulOm = () => {
      setOmSendFailed(false);
      if (selectedTransportIds.length > 0) {
        setDialogDriver(true);
        return;
      }
      handleCloseAll();
    };

    const handleSendDriverOM = async () => {
      setTextLoading("Génération et envoi de l’ordre de mission (OM) au chauffeur...");
      const selectedTransportIdSet = new Set(selectedTransportIds);
      const transportsToSend = fidTransportsEligibleForOm.filter(
        (transport) =>
          !selectedTransportIdSet.has(
            normalizeTransportId(transport.transport_id)
          )
      );

      if (transportsToSend.length === 0) {
        return true;
      }

      const excludedTransportIds = normalizeTransportIds([
        ...selectedTransportIds,
        ...fidTransportsWithoutDriver.map(
          (transport) => transport.transport_id
        ),
      ]);

      try {
        const driverOM = await MissionTransportService.sendDriverOM(
          mission_planning_id,
          mission_planning_agenda_id,
          excludedTransportIds
        );
        if (!driverOM || !driverOM.status) {
          setMessage({
            open: true,
            color: 'red',
            messageInfo:
              driverOM?.message ||
              "Erreur lors de l’envoi de l’ordre de mission au chauffeur."
          });
          return false;
        }

        setMessage({
          open: true,
          color: 'green',
          messageInfo:
            driverOM?.message ||
            "Ordre de mission envoyé avec succès."
        });
        return true;
      } catch (error) {
        console.error('Error sending driver OM:', error);
        setMessage({
          open: true,
          color: 'red',
          messageInfo: "Une erreur est survenue lors de l’envoi de l’OM chauffeur."
        });
        return false;
      }
    };

    const handleValidate = async () => {
      setLoadingButton(true);
      setOpenLoadingSimpleBackdrop(true);
      setTextLoading('Validation et régénération du timesheet de la mission...');
      try {
        const validationResult = await onConfirm();

        if (!validationResult) {
          setMessage({
            open: true,
            color: 'red',
            messageInfo: "Erreur lors de la validation du transport."
          });
          return;
        }

        setTimesheetValidated(true);
        const driverOmSent = await handleSendDriverOM();
        if (!driverOmSent) {
          setOmSendFailed(true);
          return;
        }

        continueAfterSuccessfulOm();
      } catch (error) {
        console.error('Error during confirmation process:', error);
        setMessage({
          open: true,
          color: 'red',
          messageInfo: "Une erreur est survenue lors du processus de validation."
        });
      } finally {
        setTextLoading('');
        setOpenLoadingSimpleBackdrop(false);
        setLoadingButton(false);
      }
    };

    const handleRetryDriverOM = async () => {
      setLoadingButton(true);
      setOpenLoadingSimpleBackdrop(true);
      try {
        const driverOmSent = await handleSendDriverOM();
        if (!driverOmSent) {
          setOmSendFailed(true);
          return;
        }

        continueAfterSuccessfulOm();
      } finally {
        setTextLoading('');
        setOpenLoadingSimpleBackdrop(false);
        setLoadingButton(false);
      }
    };

    const handleProceedDriverTimesheet = () => {
      if (!selectedTransportIds.length) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Veuillez sélectionner au moins un transport FID.'
        });
        return;
      }

      const searchParams = new URLSearchParams({
        transport_ids: selectedTransportIds.join(','),
      });
      navigate(
        `/Logistic/mission/timesheet/driver/${mission_planning_id}/${mission_planning_agenda_id}/${mission_timesheet_id || 0}?${searchParams.toString()}`,
        {
          state: {
            from: '/Logistic/mission/timesheet',
          },
        }
      );
    };

    const handleToggleSelectAll = () => {
      if (!fidTransportIds.length) {
        return;
      }
      if (allFidSelected) {
        setSelectedTransportIds([]);
        return;
      }
      setSelectedTransportIds(fidTransportIds);
    };

    const handleDriverElaboration = () => {
    }

    return (
        <>
             

              <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />
              <LoadingCustomBackdrop open={openLoadingSimpleBackdrop}>
                <Typography variant="h6" sx={{ marginTop: '10px' }}>
                  {textLoading} 
                </Typography>
              </LoadingCustomBackdrop>
               <Dialog
                open={openDialog && !dialogDriver}
                onClose={handleCloseAll}
                disableEscapeKeyDown
                aria-labelledby="confirmation-transport-title"
                aria-describedby="confirmation-transport-description"
                maxWidth="md"
                fullWidth
                sx={{ '& .MuiDialog-paper': { borderRadius: '16px', overflow: 'hidden' } }}
               >
        <DialogTitle id="confirmation-transport-title" sx={{color: 'white', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Typography variant="h6" component="div">
                    Confirmation de la gestion du transport
                  </Typography>
                  {reference && (
                    <Typography variant="subtitle2" style={{ color: "white" }}>
                      Mission {reference}
                    </Typography>
                  )}
                </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f7f9fc' }}>
          <DialogContentText
            id="confirmation-transport-description"
            component="div"
            sx={{ color: '#2f3b52', fontSize: '0.95rem' }}
          >
              <Typography component="div" variant="body1" mt={1} sx={{ lineHeight: 1.6 }}>
              Souhaitez-vous valider <strong>le timesheet</strong> de la mission ?<br/>
              Une fois validé,  <strong>la gestion des transports</strong> ne pourra plus être modifiée, sauf en cas de <strong>réactivation</strong>.
              <ul>
                {/* <li>L’Ordre de Mission (OM) du chauffeur sera envoyé au chauffeur.</li> */}
               {carpooling && <li>Le <strong>décompte prévisionnel du carburant</strong> de la voiture en covoiturage sera calculé en fonction du trajet effectué par le chef de bord.</li>}
                <li>Le <strong>décompte prévisionnel du carburant</strong> sera automatiquement transmis <strong>aux missionnaires</strong> pour confirmation.</li>
              </ul> 
              Merci de <strong>vérifier</strong> attentivement les informations avant de continuer.<br/>
              <Alert severity="error" sx={{ mt: 2, mb: 2, fontSize: '0.9rem' , borderLeft: "4px solid #ef4444", boxShadow: '0 4px 12px rgba(16,24,40,0.08)' }} icon={false} >
              <strong>Le timesheet du chauffeur</strong> est-il <strong>différent</strong> de celui des missionnaires ? <br/>
             Si oui, veuillez  <strong>sélectionner</strong> la(les) <strong>voiture(s) FID</strong> concernée(s) afin de générer <strong>un timesheet individuel pour chaque chauffeur.</strong> 
              </Alert>
            </Typography>

              {timesheetValidated && omSendFailed && (
                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                  Le timesheet est déjà validé, mais l’envoi d’au moins un OM chauffeur a échoué.
                  Vous pouvez fermer ce dialogue ou réessayer l’envoi sans relancer la validation.
                </Alert>
              )}

              {carpooling && (
                <Alert severity="info" sx={{ mt: 2, mb: 1, fontSize: '0.9rem' }}>
                  Ce transport est en covoiturage.
                </Alert>
              )}
               
              <TableContainer sx={{ mt: 2, border: '1px solid #e1e5ee', borderRadius: 2, overflow: 'hidden', backgroundColor: 'white', boxShadow: '0 4px 12px rgba(16,24,40,0.08)' }}>
                <Table stickyHeader size="small" aria-label="sticky table">
                  <TableHead sx={{ '& .MuiTableCell-head': { backgroundColor: '#eef3fb', color: '#1f2937', fontWeight: 700 } }}>
                    <TableRow>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Checkbox
                            size="small"
                            indeterminate={someFidSelected && !allFidSelected}
                            checked={allFidSelected}
                            onChange={handleToggleSelectAll}
                            disabled={
                              !fidTransportIds.length ||
                              loadingButton ||
                              transportLoading ||
                              timesheetValidated
                            }
                            inputProps={{ 'aria-label': 'Tout sélectionner les transports FID' }}
                          />
                        </div>
                      </TableCell>
                      <TableCell style={{width: "14%"}}><strong>Voiture</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Chauffeur</strong></TableCell>
                      <TableCell><strong>Chef de bord</strong></TableCell>
                      <TableCell><strong>Missions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ '& .MuiTableCell-body': { fontSize: '0.88rem', color: '#3b4a63' } }}>
                    {transports.map((item, index) => (
                      <TableRow
                        key={item?.transport_id || item?.vhc_id || index}
                        sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9fbff' } }}
                      >
                        <TableCell>
                          {isFidTransport(item) ? (
                            <Checkbox
                              checked={selectedTransportIds.includes(
                                normalizeTransportId(item.transport_id)
                              )}
                              onChange={() => handleToggleTransport(item.transport_id)}
                              disabled={
                                !hasValidTransportId(item) ||
                                timesheetValidated ||
                                transportLoading
                              }
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>{item?.vhc_imm ? item?.vhc_imm : ''} </TableCell>
                        <TableCell> {item?.vhc_type || item?.type || 'Location'} </TableCell>
                        <TableCell> {item?.driver_name || ''} </TableCell>
                        <TableCell> {item?.captain_name || ''} </TableCell>
                        <TableCell>
                          {(item?.missions || []).map((mission, idx) => {
                            const stateMeta = STATE_TIMESHEET.find((m) => m.value === mission.mission_timesheet_state);
                            const stateLabel = stateMeta?.label || mission.mission_timesheet_state;
                            const stateColor = stateMeta?.color || '#64748b';
                            return (
                              <div
                                key={mission?.mission_planning_id || mission?.reference || idx}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
                              >
                                <span>{mission?.reference || 'Mission'}</span>
                                {mission?.mission_timesheet_state && (
                                  <Chip
                                    label={`Timesheet ${stateLabel}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: stateColor,
                                      color: '#fff',
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

        </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', backgroundColor: '#f7f9fc', borderTop: '1px solid #e1e5ee' }}>
          {omSendFailed ? (
            <>
              <Button
                onClick={handleCloseAll}
                color="primary"
                variant="outlined"
                disabled={loadingButton}
              >
                Fermer
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleRetryDriverOM}
                disabled={loadingButton}
              >
                Réessayer l’envoi OM
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCloseAll}
                color="primary"
                variant="outlined"
                autoFocus
                disabled={loadingButton || transportLoading}
              >
                Annuler
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleValidate}
                disabled={loadingButton || transportLoading || timesheetValidated}
              >
                Valider
              </Button>
            </>
          )}
       </DialogActions>
      </Dialog>

      <Dialog
          open={openDialog && dialogDriver}
          maxWidth="sm"
          fullWidth
          aria-labelledby="driver-timesheet-title"
          aria-describedby="driver-timesheet-description"
        >
          <DialogTitle id="driver-timesheet-title" sx={{color: 'white', backgroundColor: '#1f2937', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
            Élaboration du timesheet chauffeur
          </DialogTitle>

          <DialogContent
            id="driver-timesheet-description"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 1.5,
              padding: '24px 32px',
            }}
            dividers
          >
            <Typography variant="body1">
              Qui doit élaborer le timesheet du chauffeur ?
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Veuillez indiquer si le timesheet doit être élaboré maintenant
              ou renseigné directement par le chauffeur concerné.
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              padding: '16px 24px',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleProceedDriverTimesheet}
            >
              Élaborer maintenant
            </Button>
            <Button
              variant="contained"
              onClick={handleDriverElaboration}
            >
              Confier au chauffeur
            </Button>
          </DialogActions>
        </Dialog>
              </>
    )

}
export default ConfirmationTransport;
